import {logger} from 'yougile-util'
import {Hub} from 'yougile-model'
import {BoardSettingsTab} from '@components/board/board-settings/board-settings'
import {ExtensionInColumn} from '@components/columns'
import {ContextObject} from '../context-object'
import {Item} from './item'
import {Items} from './items'
import {PlatformEvents} from './platform-events'

interface UIMenuItem {
  title: string
  onClick: () => void
}

/**
 * @description
 * Allows to intercept UI events and add custom elements to the system UI.
 * @descriptionRu
 * Позволяет перехватывать события отрисовки элементов системы и
 * добавлять свои элементы в разные части системы.
 */
export class UIHandlers extends ContextObject {
  /**
   * @code
   * UIHandlers.onColumnMenuShow = function (column) {
   *   return [];
   * };
   * @example
   * UIHandlers.onColumnMenuShow = function (column) {
   *   return [
   *      {
   *        title: 'Menu name',
   *        onClick: function () {
   *          Notifier.success('Click');
   *        }
   *      }
   *   ];
   * };
   * @description
   * This function runs when user opens column menu. If you return
   * array of menu items then they are added to the menu.
   * @descriptionRu
   * Эта функция вызывается когда пользователь открывает меню колонки.
   * Если фукция возвращает массив элементов меню, то
   * в меню добавляются пункты.
   */
  public onColumnMenuShow?: (column: Item) => UIMenuItem[]

  public static columnMenuShow(col: Hub): UIMenuItem[] {
    const res: UIMenuItem[] = []
    this.forEachCurrent((h: UIHandlers) => {
      if (!h?.onColumnMenuShow) {
        return true
      }
      try {
        const r = h.onColumnMenuShow(Items.fromHub(col))
        if (r && r.length > 0) {
          r.forEach(e => {
            if (e?.title) {
              res.push(e)
            }
          })
        }
      } catch (e) {
        logger.error('Error in configurator onColumnMenuShow', e)
      }
      return true
    })
    return res
  }

  private _onExtensionInColumn?: (column: Item) => ExtensionInColumn[]

  /**
   * @code
   * UIHandlers.onExtensionInColumn = function (column) {
   *   return [];
   * };
   * @example
   * UIHandlers.onExtensionInColumn = function (column) {
   *   return [
   *      {
   *        id: 'ext-task-approval',
   *        title: 'Согласование задач в колонке'
   *      }
   *   ];
   * };
   *
   * @description
   * This function is triggered when the column interface are renders. If you return an array
   * of extensions that related to this column, they are added to the icon "Extensions in the column",
   * that helps user to understand, which extensions active in this column at this moment.
   * @descriptionRu
   * Эта функция вызывается, когда отображается интерфейс колонки. Если функция возвращает
   * массив расширений которые используют эту колонку, они добавляются в иконку "Расширения в колонке",
   * которая позволяет пользователю понять, какие расширения активны в этой колонке в данный момент.
   */
  public set onExtensionInColumn(
    value: ((column: Item) => ExtensionInColumn[]) | undefined
  ) {
    this._onExtensionInColumn = value
    PlatformEvents.trigger(PlatformEvents.events.updateExtensionsInColumn)
  }

  public get onExtensionInColumn():
    | ((column: Item) => ExtensionInColumn[])
    | undefined {
    return this._onExtensionInColumn
  }

  public static extensionsInColumn(column: Hub): ExtensionInColumn[] {
    const res: ExtensionInColumn[] = []
    this.forEachCurrent((h: UIHandlers) => {
      if (!h?.onExtensionInColumn) {
        return true
      }
      try {
        const r = h.onExtensionInColumn(Items.fromHub(column))
        if (r && r.length > 0) {
          r.forEach(e => {
            res.push(e)
          })
        }
      } catch (e) {
        logger.error('Error in configurator onExtensionInColumn', e)
      }
      return true
    })
    return res
  }

  /**
   * @code
   * UIHandlers.onBoardSettings = function (board) {
   *   return [];
   * };
   * @example
   * UIHandlers.onBoardSettings = function (board) {
   *   return [
   *      {
   *        id: 'ext-forms',
   *        title: 'Шаблоны форм',
   *        disabled: false,
   *        helpText: 'Помощь'
   *      }
   *   ];
   * };
   *
   * @description
   * This function is triggered when the board settings are accessed. If you return
   * an array of settings tabs, they are added to the board settings interface.
   * @descriptionRu
   * Эта функция вызывается, когда открываются настройки доски. Если функция возвращает
   * массив вкладок настроек, они добавляются в интерфейс настроек доски.
   */
  public onBoardSettings?: (board: Item) => BoardSettingsTab[]

  public static boardSettings(board: Hub): BoardSettingsTab[] {
    const res: BoardSettingsTab[] = []
    this.forEachCurrent((h: UIHandlers) => {
      if (!h?.onBoardSettings) {
        return true
      }
      try {
        const r = h.onBoardSettings(Items.fromHub(board))
        if (r && r.length > 0) {
          r.forEach(e => {
            res.push(e)
          })
        }
      } catch (e) {
        logger.error('Error in configurator onBoardSettings', e)
      }
      return true
    })
    return res
  }

  /**
   * @code
   * UIHandlers.onTriggerTaskDone = function (task) {};
   * @example
   * UIHandlers.onBoardSettings = function (task) {};
   *
   * @description
   * This function is triggered when the task completion is changed.
   * @descriptionRu
   * Эта функция вызывается, когда статус выполнения задачи изменяется.
   */
  public onTriggerTaskDone?: (task: Item) => void

  public static triggerTaskDone(task: Hub): void {
    this.forEachCurrent((h: UIHandlers) => {
      if (!h?.onTriggerTaskDone) {
        return true
      }
      try {
        h.onTriggerTaskDone(Items.fromHub(task))
      } catch (e) {
        logger.error('Error in configurator onTriggerTaskDone', e)
      }
      return true
    })
  }
}
